<template>
  <div class="audit-reports-card">
    <!-- Header without global sync button -->
    <div class="card-header">
      <h3 class="card-title">
        <i class="fas fa-chart-line text-indigo-600 mr-2" />
        Performance Reports
      </h3>
      <div class="header-actions">
        <span
          v-if="Object.keys(syncingLocations).length > 0"
          class="sync-status">
          <i class="fas fa-spinner fa-spin text-indigo-600 mr-1" />
          Syncing {{ Object.keys(syncingLocations).length }} location(s)...
        </span>
      </div>
    </div>

    <!-- Loading state -->
    <div
      v-if="loading && !syncing"
      class="loading-container">
      <i class="fas fa-spinner fa-spin text-2xl text-indigo-600" />
      <p class="mt-2 text-gray-600">
        Loading reports...
      </p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!loading && groupedReports.length === 0"
      class="empty-state">
      <i class="fas fa-file-alt text-4xl text-gray-400 mb-3" />
      <p class="text-gray-600 mb-4">
        No performance reports available
      </p>
      <p class="text-sm text-gray-500">
        Reports will appear here once locations are synced
      </p>
    </div>

    <!-- Sync progress -->
    <div
      v-if="syncProgress"
      class="sync-progress">
      <div class="progress-header">
        <i class="fas fa-sync fa-spin text-indigo-600 mr-2" />
        <span>{{ syncProgress.status }}</span>
      </div>
      <div
        v-if="syncProgress.progress"
        class="progress-bar-container">
        <div
          class="progress-bar"
          :style="`width: ${syncProgress.progress}%`" />
      </div>
      <div
        v-if="syncProgress.currentLocation"
        class="text-sm text-gray-600 mt-2">
        Processing: {{ syncProgress.currentLocation }}
      </div>
    </div>

    <!-- Reports list grouped by location -->
    <div
      v-else-if="groupedReports.length > 0"
      class="reports-container">
      <div
        v-for="location in groupedReports"
        :key="location.id"
        class="location-section">
        <div
          class="location-header"
          @click="toggleLocation(location.id)">
          <div class="location-info">
            <i
              :class="expandedLocations[location.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
              class="mr-2 text-gray-500" />
            <h4 class="location-name">
              {{ location.name }}
            </h4>
            <span class="report-count">{{ location.reports.length }} reports</span>
            <span
              v-if="location.averageQuality"
              :class="getQualityBadgeClass(location.averageQuality)"
              class="quality-badge">
              Avg: {{ location.averageQuality }}%
            </span>
          </div>
          <div class="location-actions">
            <button
              v-if="location.hasRemoteId"
              class="sync-location-button"
              :disabled="syncing || syncingLocations[location.id]"
              @click.stop="syncLocationReports(location.id, location.name)">
              <i :class="syncingLocations[location.id] ? 'fas fa-spinner fa-spin' : 'fas fa-sync'" />
              {{ syncingLocations[location.id] ? 'Syncing...' : 'Sync' }}
            </button>
            <span
              v-else
              class="text-xs text-gray-500">
              <i class="fas fa-exclamation-triangle text-yellow-500 mr-1" />
              No remote ID configured
            </span>
            <span
              v-if="location.lastSynced"
              class="text-xs text-gray-500 ml-2">
              Last: {{ formatDate(location.lastSynced) }}
            </span>
          </div>
        </div>

        <transition name="expand">
          <div
            v-if="expandedLocations[location.id]"
            class="reports-list">
            <div
              v-for="report in location.reports"
              :key="report.id"
              class="report-item"
              @click="toggleReport(report.id)">
              <div class="report-header">
                <div class="report-main-info">
                  <span
                    :class="getStatusBadgeClass(report.status)"
                    class="status-badge">
                    {{ formatStatus(report.status) }}
                  </span>
                  <span class="report-keyword">{{ report.keyword || 'No keyword' }}</span>
                  <span
                    v-if="report.reportType === 'recurring'"
                    class="report-type-badge recurring">
                    <i class="fas fa-sync-alt mr-1" />
                    Recurring
                  </span>
                  <span
                    v-else
                    class="report-type-badge one-off">
                    <i class="fas fa-file mr-1" />
                    One-off
                  </span>
                </div>
                <div class="report-meta">
                  <span
                    v-if="report.qualityScore !== null"
                    :class="getQualityScoreClass(report.qualityScore)"
                    class="quality-score">
                    <i class="fas fa-chart-line mr-1" />
                    {{ report.qualityScore }}%
                  </span>
                  <span class="report-date">{{ formatDate(report.reportCreatedAt) }}</span>
                  <i
                    :class="expandedReports[report.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                    class="expand-icon" />
                </div>
              </div>

              <!-- Expanded report details -->
              <transition name="slide">
                <div
                  v-if="expandedReports[report.id]"
                  class="report-details"
                  @click.stop>
                  <div class="detail-grid">
                    <div class="detail-item">
                      <label>Configuration:</label>
                      <span>{{ report.configurationName || 'N/A' }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Created by:</label>
                      <span>{{ report.createdBy || 'System' }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Completion:</label>
                      <span>{{ report.completionPercentage }}%</span>
                    </div>
                    <div
                      v-if="report.reportFinishedAt"
                      class="detail-item">
                      <label>Finished:</label>
                      <span>{{ formatDate(report.reportFinishedAt) }}</span>
                    </div>
                  </div>

                  <!-- Warnings and Failures -->
                  <div
                    v-if="report.hasWarnings"
                    class="warnings-section">
                    <h5 class="section-title">
                      <i class="fas fa-exclamation-triangle text-yellow-600 mr-1" />
                      Warnings
                    </h5>
                    <ul
                      v-if="reportDetails[report.id]"
                      class="warning-list">
                      <li
                        v-for="(warning, index) in reportDetails[report.id].reportWarnings"
                        :key="index">
                        {{ warning }}
                      </li>
                    </ul>
                  </div>

                  <div
                    v-if="report.hasFailures"
                    class="failures-section">
                    <h5 class="section-title">
                      <i class="fas fa-times-circle text-red-600 mr-1" />
                      Failed Sources
                    </h5>
                    <ul
                      v-if="reportDetails[report.id]"
                      class="failure-list">
                      <li
                        v-for="(failure, index) in reportDetails[report.id].failedReportSources"
                        :key="index">
                        {{ failure }}
                      </li>
                    </ul>
                  </div>

                  <!-- Debug: Show raw data status -->
                  <div
                    v-if="reportDetails[report.id]"
                    class="text-xs text-gray-500 mt-2">
                    Debug: Report details loaded -
                    rawData: {{ reportDetails[report.id].rawData ? 'YES' : 'NO' }}
                    <span v-if="reportDetails[report.id].rawData">
                      | charts: {{ reportDetails[report.id].rawData.charts ? 'YES' : 'NO' }}
                      <span v-if="reportDetails[report.id].rawData.charts">
                        | stats: {{
                          reportDetails[report.id].rawData.charts.stats
                            ? `YES (${reportDetails[report.id].rawData.charts.stats.length})`
                            : 'NO'
                        }}
                      </span>
                    </span>
                  </div>

                  <!-- Stats Cards -->
                  <div
                    v-if="reportDetails[report.id]
                      && reportDetails[report.id].rawData
                      && reportDetails[report.id].rawData.charts
                      && reportDetails[report.id].rawData.charts.stats"
                    class="stats-section">
                    <h5 class="section-title">
                      <i class="fas fa-chart-bar text-indigo-600 mr-1" />
                      Performance Metrics
                    </h5>
                    <div class="stats-grid">
                      <div
                        v-for="stat in reportDetails[report.id].rawData.charts.stats.slice(0, 6)"
                        :key="stat.id"
                        class="stat-card">
                        <div class="stat-name">
                          {{ formatStatName(stat.name) }}
                        </div>
                        <div class="stat-value">
                          {{ (stat.sum || 0).toLocaleString() }}
                        </div>
                        <div
                          v-if="stat.sum_compare !== undefined && stat.sum_compare !== null"
                          class="stat-comparison">
                          <span :class="getChangeClass(stat.sum, stat.sum_compare)">
                            <i :class="getChangeIcon(stat.sum, stat.sum_compare)" />
                            {{ getChangePercent(stat.sum, stat.sum_compare) }}%
                          </span>
                          <span class="text-gray-500 text-xs ml-1">
                            vs {{ stat.sum_compare.toLocaleString() }}
                          </span>
                        </div>
                        <div
                          v-else
                          class="stat-comparison">
                          <span class="text-gray-500 text-xs">No comparison data</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Debug Keywords -->
                  <div
                    v-if="reportDetails[report.id] && reportDetails[report.id].rawData"
                    class="text-xs text-gray-500 mt-2">
                    Keywords data: {{ reportDetails[report.id].rawData.keywords ? 'YES' : 'NO' }}
                    <span v-if="reportDetails[report.id].rawData.keywords">
                      | byLocations: {{ reportDetails[report.id].rawData.keywords.byLocations ? 'YES' : 'NO' }}
                      | by_locations: {{ reportDetails[report.id].rawData.keywords.by_locations ? 'YES' : 'NO' }}
                    </span>
                  </div>

                  <!-- Keyword Rankings -->
                  <div
                    v-if="reportDetails[report.id] && reportDetails[report.id].rawData && reportDetails[report.id].rawData.keywords"
                    class="keywords-section mt-4">
                    <h5 class="section-title">
                      <i class="fas fa-search text-purple-600 mr-1" />
                      Keyword Rankings
                    </h5>
                    <div class="keywords-table mt-3">
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="border-b">
                            <th class="text-left py-2">
                              Search Term
                            </th>
                            <th class="text-center py-2">
                              Local
                            </th>
                            <th class="text-center py-2">
                              Organic
                            </th>
                            <th class="text-center py-2">
                              Mobile
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="keyword in getKeywords(reportDetails[report.id].rawData.keywords).slice(0, 5)"
                            :key="keyword.search_term"
                            class="border-b">
                            <td class="py-2">
                              {{ keyword.search_term }}
                            </td>
                            <td class="text-center py-2">
                              <span :class="getRankClass(keyword.local_rank)">
                                {{ formatRank(keyword.local_rank) }}
                              </span>
                            </td>
                            <td class="text-center py-2">
                              <span :class="getRankClass(keyword.organic_rank)">
                                {{ formatRank(keyword.organic_rank) }}
                              </span>
                            </td>
                            <td class="text-center py-2">
                              <span :class="getRankClass(keyword.mobile_rank)">
                                {{ formatRank(keyword.mobile_rank) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Reviews Summary -->
                  <div
                    v-if="reportDetails[report.id] && reportDetails[report.id].rawData && reportDetails[report.id].rawData.reviews"
                    class="reviews-summary mt-4">
                    <h5 class="section-title">
                      <i class="fas fa-star text-yellow-500 mr-1" />
                      Reviews Summary
                    </h5>
                    <div class="grid grid-cols-2 gap-4 mt-3">
                      <div class="stat-card">
                        <div class="stat-name">
                          Total Reviews
                        </div>
                        <div class="stat-value">
                          {{ getReviewsData(reportDetails[report.id].rawData.reviews).total }}
                        </div>
                        <div class="stat-comparison">
                          <span
                            :class="getChangeClass(
                              getReviewsData(reportDetails[report.id].rawData.reviews).total,
                              getReviewsData(reportDetails[report.id].rawData.reviews).totalCompare
                            )">
                            <i
                              :class="getChangeIcon(
                                getReviewsData(reportDetails[report.id].rawData.reviews).total,
                                getReviewsData(reportDetails[report.id].rawData.reviews).totalCompare
                              )" />
                            {{ getChangePercent(
                              getReviewsData(reportDetails[report.id].rawData.reviews).total,
                              getReviewsData(reportDetails[report.id].rawData.reviews).totalCompare
                            ) }}%
                          </span>
                          <span class="text-gray-500 text-xs ml-1">
                            vs {{ getReviewsData(reportDetails[report.id].rawData.reviews).totalCompare }}
                          </span>
                        </div>
                      </div>
                      <div class="stat-card">
                        <div class="stat-name">
                          Unanswered
                        </div>
                        <div class="stat-value">
                          {{ getReviewsData(reportDetails[report.id].rawData.reviews).unanswered }}
                        </div>
                        <div class="stat-comparison">
                          <span class="text-gray-500 text-xs">
                            vs {{ getReviewsData(reportDetails[report.id].rawData.reviews).unansweredCompare }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- View in Local Viking -->
                  <div class="report-actions">
                    <a
                      :href="`${localVikingBaseUrl}/reports/${report.remoteReportId}`"
                      target="_blank"
                      class="btn-link">
                      <i class="fas fa-external-link-alt mr-1" />
                      View in Local Viking
                    </a>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Metrics summary -->
    <div
      v-if="metrics && !loading"
      class="metrics-summary">
      <div class="metric-item">
        <span class="metric-label">Total Reports:</span>
        <span class="metric-value">{{ metrics.totalReports }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">Average Quality:</span>
        <span
          :class="getQualityScoreClass(metrics.averageQualityScore)"
          class="metric-value">
          {{ metrics.averageQualityScore }}%
        </span>
      </div>
      <div
        v-if="metrics.reportsWithWarnings > 0"
        class="metric-item">
        <span class="metric-label">With Warnings:</span>
        <span class="metric-value text-yellow-600">{{ metrics.reportsWithWarnings }}</span>
      </div>
      <div
        v-if="metrics.failedReports > 0"
        class="metric-item">
        <span class="metric-label">Failed:</span>
        <span class="metric-value text-red-600">{{ metrics.failedReports }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { createConsumer } from '@rails/actioncable'
import axios from '../init/axios'

export default {
  name: 'AuditReportsCard',
  props: {
    organizationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      syncing: false,
      syncingLocations: {}, // Track which locations are syncing
      locations: [], // All locations for the organization
      reports: [],
      metrics: null,
      expandedLocations: {},
      expandedReports: {},
      reportDetails: {},
      syncProgress: null,
      subscription: null,
      localVikingBaseUrl: 'https://app.localviking.com'
    }
  },
  computed: {
    groupedReports() {
      const grouped = {}

      // Start with all locations
      this.locations.forEach((location) => {
        grouped[location.id] = {
          id: location.id,
          name: location.name,
          hasRemoteId: location.hasRemoteId,
          reports: [],
          lastSynced: null,
          totalQuality: 0,
          qualityCount: 0
        }
      })

      // Add reports to their locations
      this.reports.forEach((report) => {
        const locationId = report.locationId
        if (grouped[locationId]) {
          grouped[locationId].reports.push(report)

          if (report.syncedAt && (!grouped[locationId].lastSynced || report.syncedAt > grouped[locationId].lastSynced)) {
            grouped[locationId].lastSynced = report.syncedAt
          }

          if (report.qualityScore !== null) {
            grouped[locationId].totalQuality += report.qualityScore
            grouped[locationId].qualityCount += 1
          }
        }
      })

      // Calculate average quality and sort reports
      Object.values(grouped).forEach((location) => {
        if (location.qualityCount > 0) {
          location.averageQuality = Math.round(location.totalQuality / location.qualityCount)
        }
        // Sort reports by date, newest first
        location.reports.sort((a, b) => new Date(b.reportCreatedAt) - new Date(a.reportCreatedAt))
      })

      return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  mounted() {
    this.loadLocations()
    this.loadReports()
    this.loadMetrics()
    this.setupActionCable()
  },
  beforeUnmount() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  },
  methods: {
    async loadLocations() {
      try {
        const response = await axios.get(`/ajax/organizations/${this.organizationId}/locations`)

        if (response.data.success) {
          this.locations = response.data.data
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load locations:', error)
      }
    },

    async loadReports() {
      try {
        this.loading = true
        const response = await axios.get(`/ajax/organizations/${this.organizationId}/audit_reports`)

        if (response.data.success) {
          this.reports = response.data.data
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load audit reports:', error)
      } finally {
        this.loading = false
      }
    },

    async loadMetrics() {
      try {
        const response = await axios.get(`/ajax/organizations/${this.organizationId}/audit_reports/metrics`)

        if (response.data.success) {
          this.metrics = response.data.data
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load metrics:', error)
      }
    },

    async syncLocationReports(locationId, locationName) {
      if (this.syncingLocations[locationId]) return

      try {
        this.syncingLocations[locationId] = true

        const response = await axios.post(`/ajax/organizations/${this.organizationId}/audit_reports/sync`, {
          analyze: true,
          location_id: locationId
        })

        if (response.data.success) {
          // Sync initiated for this location
          // eslint-disable-next-line no-console
          console.log(`Sync initiated for ${locationName}. Job ID: ${response.data.jobId}`)
        } else {
          // eslint-disable-next-line no-alert
          alert(`Failed to sync reports for ${locationName}: ${response.data.error || 'Unknown error'}`)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to sync reports for location ${locationId}:`, error)
        // eslint-disable-next-line no-alert
        alert(`Failed to sync reports for ${locationName}`)
      } finally {
        // Will be set to false when sync completes via ActionCable
        setTimeout(() => {
          this.syncingLocations[locationId] = false
        }, 30000) // Timeout after 30 seconds as fallback
      }
    },

    async loadReportDetails(reportId) {
      if (this.reportDetails[reportId]) return

      try {
        const response = await axios.get(`/ajax/organizations/${this.organizationId}/audit_reports/${reportId}`)

        if (response.data.success) {
          // Use Vue 3's reactivity properly
          this.reportDetails = {
            ...this.reportDetails,
            [reportId]: response.data.data
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load report details:', error)
      }
    },

    setupActionCable() {
      const consumer = createConsumer()

      this.subscription = consumer.subscriptions.create(
        { channel: 'AuditReportsChannel', organization_id: this.organizationId },
        {
          connected() {
            // eslint-disable-next-line no-console
            console.log('Connected to AuditReportsChannel')
          },

          disconnected() {
            // eslint-disable-next-line no-console
            console.log('Disconnected from AuditReportsChannel')
          },

          received: (data) => {
            this.handleChannelMessage(data)
          }
        }
      )
    },

    handleChannelMessage(data) {
      // eslint-disable-next-line no-console
      console.log('Received ActionCable message:', data)

      // Handle sync_progress event with different statuses
      if (data.event === 'sync_progress') {
        switch (data.status) {
          case 'started':
            this.syncProgress = { status: 'Sync started...', progress: 0 }
            break

          case 'in_progress':
            if (data.data) {
              this.syncProgress = {
                status: `Syncing... (${data.data.successful_syncs}/${data.data.total_locations} locations)`,
                progress: data.data.progress || 0,
                currentLocation: data.data.current_location
              }

              // Update per-location syncing state if current_location is provided
              if (data.data.current_location) {
                const location = this.locations.find((l) => l.name === data.data.current_location)
                if (location) {
                  this.syncingLocations[location.id] = true
                }
              }
            }
            break

          case 'completed':
            this.syncing = false
            this.syncProgress = null
            // Clear any per-location syncing states
            this.syncingLocations = {}

            // Show summary if there were errors
            if (data.data?.errors?.length > 0) {
              const errorMessages = data.data.errors
                .map((e) => `${e.location_name}: ${e.error}${e.details ? ` - ${e.details}` : ''}`)
                .join('\n')
              // eslint-disable-next-line no-alert
              alert(`Sync completed with errors:\n${errorMessages}`)
            }

            this.loadReports()
            this.loadMetrics()
            this.loadLocations()
            break

          case 'failed':
            this.syncing = false
            this.syncProgress = null
            // Clear any per-location syncing states
            this.syncingLocations = {}
            // eslint-disable-next-line no-alert
            alert(`Sync failed: ${data.data?.error || 'Unknown error'}`)
            break

          default:
            // eslint-disable-next-line no-console
            console.warn('Unknown sync status:', data.status, data)
            break
        }
      } else if (data.event === 'quality_alert') {
        // Handle quality alerts if needed
        // eslint-disable-next-line no-console
        console.warn('Quality alert:', data)
      }
    },

    toggleLocation(locationId) {
      this.expandedLocations[locationId] = !this.expandedLocations[locationId]
    },

    async toggleReport(reportId) {
      this.expandedReports[reportId] = !this.expandedReports[reportId]

      if (this.expandedReports[reportId] && !this.reportDetails[reportId]) {
        await this.loadReportDetails(reportId)
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'

      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`

      return date.toLocaleDateString()
    },

    formatStatus(status) {
      const statusMap = {
        initialized: 'Initialized',
        collecting_data: 'Collecting',
        finished: 'Finished',
        finished_with_warnings: 'Finished*',
        failed: 'Failed'
      }
      return statusMap[status] || status
    },

    getStatusBadgeClass(status) {
      const classes = {
        initialized: 'bg-gray-100 text-gray-700',
        collecting_data: 'bg-blue-100 text-blue-700',
        finished: 'bg-green-100 text-green-700',
        finished_with_warnings: 'bg-yellow-100 text-yellow-700',
        failed: 'bg-red-100 text-red-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-700'
    },

    getQualityBadgeClass(score) {
      if (score >= 90) return 'bg-green-100 text-green-700'
      if (score >= 75) return 'bg-blue-100 text-blue-700'
      if (score >= 50) return 'bg-yellow-100 text-yellow-700'
      return 'bg-red-100 text-red-700'
    },

    getQualityScoreClass(score) {
      if (score >= 90) return 'text-green-600'
      if (score >= 75) return 'text-blue-600'
      if (score >= 50) return 'text-yellow-600'
      return 'text-red-600'
    },

    formatStatName(name) {
      // Simplify stat names for display
      return name
        .replace('Business ', '')
        .replace('impressions ', '')
        .replace('desktop ', 'Desktop ')
        .replace('mobile ', 'Mobile ')
        .replace('search', 'Search')
        .replace('maps', 'Maps')
        .replace('direction requests', 'Directions')
    },

    getChangeClass(current, previous) {
      if (previous === undefined || previous === null || previous === 0) return 'text-gray-500'
      const change = ((current - previous) / previous) * 100
      if (change > 0) return 'text-green-600'
      if (change < 0) return 'text-red-600'
      return 'text-gray-500'
    },

    getChangeIcon(current, previous) {
      if (previous === undefined || previous === null || previous === 0) return 'fas fa-minus'
      const change = current - previous
      if (change > 0) return 'fas fa-arrow-up'
      if (change < 0) return 'fas fa-arrow-down'
      return 'fas fa-minus'
    },

    getChangePercent(current, previous) {
      if (previous === undefined || previous === null || previous === 0) return '0'
      const change = ((current - previous) / previous) * 100
      return change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1)
    },

    getKeywords(keywordsData) {
      // Handle both camelCase and snake_case
      const byLocations = keywordsData?.byLocations || keywordsData?.by_locations

      if (!keywordsData || !byLocations) {
        return []
      }

      // Get keywords from the first location
      const locationKey = Object.keys(byLocations)[0]
      if (!locationKey) return []

      const keywords = byLocations[locationKey]
      if (!Array.isArray(keywords)) {
        return []
      }

      // Transform to simpler format - handle camelCase in the data
      return keywords.map((k) => ({
        search_term: k.searchTerm || k.search_term,
        local_rank: this.getCurrentRank(k.ranks?.local),
        organic_rank: this.getCurrentRank(k.ranks?.organic),
        mobile_rank: this.getCurrentRank(k.ranks?.organicMobile || k.ranks?.organic_mobile)
      }))
    },

    getCurrentRank(rankData) {
      if (!rankData || !rankData.data || !rankData.data[0]) return null
      // Handle both camelCase and snake_case
      return rankData.data[0].currentPosition || rankData.data[0].current_position
    },

    formatRank(rank) {
      if (rank === null || rank === undefined) return '—'
      if (rank <= 3) return `#${rank}`
      return rank.toString()
    },

    getRankClass(rank) {
      if (rank === null || rank === undefined) return 'text-gray-400'
      if (rank <= 3) return 'text-green-600 font-semibold'
      if (rank <= 10) return 'text-blue-600'
      if (rank <= 20) return 'text-gray-600'
      return 'text-gray-400'
    },

    getReviewsData(reviewsData) {
      if (!reviewsData || !reviewsData.aggregated || !reviewsData.aggregated.sum) {
        return {
          total: 0, totalCompare: 0, unanswered: 0, unansweredCompare: 0
        }
      }

      const sum = reviewsData.aggregated.sum
      // Handle both camelCase and snake_case
      const compareData = sum.compareData || sum.compare_data

      return {
        total: sum.data?.total || 0,
        totalCompare: compareData?.total || 0,
        unanswered: sum.data?.unanswered || 0,
        unansweredCompare: compareData?.unanswered || 0
      }
    }
  }
}
</script>

<style scoped>
.audit-reports-card {
  @apply bg-white rounded-lg;
}

.card-header {
  @apply flex justify-between items-center mb-4;
}

.card-title {
  @apply text-lg font-semibold text-gray-900 flex items-center;
}

.header-actions {
  @apply flex gap-2;
}

.sync-button {
  @apply px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700
         disabled:opacity-50 disabled:cursor-not-allowed transition-colors
         flex items-center gap-2 text-sm font-medium;
}

.sync-location-button {
  @apply px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700
         disabled:opacity-50 disabled:cursor-not-allowed transition-colors
         flex items-center gap-1 font-medium;
}

.loading-container {
  @apply flex flex-col items-center justify-center py-12;
}

.empty-state {
  @apply flex flex-col items-center justify-center py-12 text-center;
}

.btn-primary {
  @apply px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700
         disabled:opacity-50 disabled:cursor-not-allowed transition-colors;
}

.sync-progress {
  @apply bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4;
}

.progress-header {
  @apply flex items-center text-blue-700 font-medium;
}

.progress-bar-container {
  @apply w-full bg-gray-200 rounded-full h-2 mt-2;
}

.progress-bar {
  @apply bg-blue-600 h-2 rounded-full transition-all duration-300;
}

.reports-container {
  @apply space-y-4;
}

.location-section {
  @apply border border-gray-200 rounded-lg overflow-hidden;
}

.location-header {
  @apply flex justify-between items-center p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors;
}

.location-info {
  @apply flex items-center gap-3;
}

.location-name {
  @apply font-medium text-gray-900;
}

.report-count {
  @apply text-sm text-gray-500;
}

.quality-badge {
  @apply px-2 py-0.5 rounded-full text-xs font-medium;
}

.reports-list {
  @apply divide-y divide-gray-200;
}

.report-item {
  @apply p-4 hover:bg-gray-50 cursor-pointer transition-colors;
}

.report-header {
  @apply flex justify-between items-start;
}

.report-main-info {
  @apply flex items-center gap-3 flex-wrap;
}

.status-badge {
  @apply px-2 py-0.5 rounded-full text-xs font-medium;
}

.report-keyword {
  @apply font-medium text-gray-900;
}

.report-type-badge {
  @apply px-2 py-0.5 rounded-full text-xs font-medium;
}

.report-type-badge.recurring {
  @apply bg-purple-100 text-purple-700;
}

.report-type-badge.one-off {
  @apply bg-gray-100 text-gray-700;
}

.report-meta {
  @apply flex items-center gap-3 text-sm;
}

.quality-score {
  @apply font-medium;
}

.report-date {
  @apply text-gray-500;
}

.expand-icon {
  @apply text-gray-400;
}

.report-details {
  @apply mt-4 p-4 bg-gray-50 rounded-lg;
}

.detail-grid {
  @apply grid grid-cols-2 gap-4 mb-4;
}

.detail-item {
  @apply text-sm;
}

.detail-item label {
  @apply font-medium text-gray-600 mr-2;
}

.warnings-section,
.failures-section {
  @apply mt-4;
}

.section-title {
  @apply text-sm font-medium text-gray-700 mb-2 flex items-center;
}

.warning-list,
.failure-list {
  @apply list-disc list-inside text-sm text-gray-600 space-y-1;
}

.stats-section {
  @apply mt-4 mb-4;
}

.stats-grid {
  @apply grid grid-cols-2 md:grid-cols-3 gap-3 mt-3;
}

.stat-card {
  @apply bg-gray-50 border border-gray-200 rounded-lg p-3;
}

.stat-name {
  @apply text-xs text-gray-600 mb-1;
}

.stat-value {
  @apply text-xl font-bold text-gray-900;
}

.stat-comparison {
  @apply text-xs mt-1 flex items-center;
}

.report-actions {
  @apply mt-4 flex gap-2;
}

.btn-link {
  @apply text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center;
}

.metrics-summary {
  @apply mt-6 pt-4 border-t border-gray-200 flex flex-wrap gap-4;
}

.metric-item {
  @apply flex items-center gap-2 text-sm;
}

.keywords-section {
  @apply mt-4 mb-4;
}

.keywords-table {
  @apply bg-gray-50 rounded-lg p-3;
}

.keywords-table table {
  @apply w-full text-sm;
}

.keywords-table th {
  @apply text-xs text-gray-600 font-medium uppercase tracking-wider;
}

.keywords-table td {
  @apply text-gray-900;
}

.reviews-summary {
  @apply mt-4 mb-4;
}

.metric-label {
  @apply text-gray-600;
}

.metric-value {
  @apply font-medium text-gray-900;
}

/* Transitions */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
